import { isNil, get, isEmpty } from 'lodash';
import { SUBTASK_PARAM_TYPES } from './taskConstants';

/**
 * Gets the subtask parameter id.
 *
 * @param {object} subtaskParameter - The subtask parameter object.
 * @returns {string} The id for the subtask parameter or empty string if not found.
 * @throws {Error} If subtask parameter is null or undefined.
 */
export const getId = subtaskParameter => {
  if (isNil(subtaskParameter)) {
    throw new Error('subtaskParameter not defined');
  }
  return get(subtaskParameter, 'id', '');
};

/**
 * Gets the subtask parameter type.
 *
 * @param {object} subtaskParameter - The subtask parameter object.
 * @returns {string} The type for the subtask parameter or empty string if not found.
 * @throws {Error} If subtask parameter is null or undefined.
 */
export const getType = subtaskParameter => {
  if (isNil(subtaskParameter)) {
    throw new Error('subtaskParameter not defined');
  }
  return get(subtaskParameter, 'type', '');
};

/**
 * Checks if the subtask parameter is a string.
 *
 * @param {object} subtaskParameter - The subtask parameter object.
 * @returns {boolean} True if the subtask parameter is a string, false otherwise.
 * @throws {Error} If subtask parameter is null or undefined.
 */
export const isString = subtaskParameter => {
  if (isNil(subtaskParameter)) {
    throw new Error('subtaskParameter not defined');
  }
  return get(subtaskParameter, 'type', '') === SUBTASK_PARAM_TYPES.STRING;
};

/**
 * Checks if the subtask parameter is a number.
 *
 * @param {object} subtaskParameter - The subtask parameter object.
 * @returns {boolean} True if the subtask parameter is a number, false otherwise.
 * @throws {Error} If subtask parameter is null or undefined.
 */
export const isNumber = subtaskParameter => {
  if (isNil(subtaskParameter)) {
    throw new Error('subtaskParameter not defined');
  }
  return get(subtaskParameter, 'type', '') === SUBTASK_PARAM_TYPES.NUMBER;
};

/**
 * Checks if the subtask parameter is a boolean.
 *
 * @param {object} subtaskParameter - The subtask parameter object.
 * @returns {boolean} True if the subtask parameter is a boolean, false otherwise.
 * @throws {Error} If subtask parameter is null or undefined.
 */
export const isBoolean = subtaskParameter => {
  if (isNil(subtaskParameter)) {
    throw new Error('subtaskParameter not defined');
  }
  return get(subtaskParameter, 'type', '') === SUBTASK_PARAM_TYPES.BOOLEAN;
};

/**
 * Checks if the subtask parameter is a date.
 *
 * @param {object} subtaskParameter - The subtask parameter object.
 * @returns {boolean} True if the subtask parameter is a date, false otherwise.
 * @throws {Error} If subtask parameter is null or undefined.
 */
export const isDate = subtaskParameter => {
  if (isNil(subtaskParameter)) {
    throw new Error('subtaskParameter not defined');
  }
  return get(subtaskParameter, 'type', '') === SUBTASK_PARAM_TYPES.DATE;
};

/**
 * Gets the subtask parameter label.
 *
 * @param {object} subtaskParameter - The subtask parameter object.
 * @returns {string} The label for the subtask parameter or empty string if not found.
 * @throws {Error} If subtask parameter is null or undefined.
 */
export const getLabel = subtaskParameter => {
  if (isNil(subtaskParameter)) {
    throw new Error('subtaskParameter not defined');
  }
  return get(subtaskParameter, 'label', '');
};

/**
 * Gets the subtask parameter options.
 *
 * @param {object} subtaskParameter - The subtask parameter object.
 * @returns {string[]} The options for the subtask parameter or empty array if not found.
 * @throws {Error} If subtask parameter is null or undefined.
 */
export const getSelectOptions = subtaskParameter => {
  if (isNil(subtaskParameter)) {
    throw new Error('subtaskParameter not defined');
  }
  return get(subtaskParameter, 'options', []);
};

/**
 * Checks if the subtask parameter has options.
 *
 * @param {object} subtaskParameter - The subtask parameter object.
 * @returns {boolean} True if the subtask parameter has options, false otherwise.
 * @throws {Error} If subtask parameter is null or undefined.
 */
export const hasOptions = subtaskParameter => {
  if (isNil(subtaskParameter)) {
    throw new Error('subtaskParameter not defined');
  }
  return !isEmpty(getSelectOptions(subtaskParameter));
};

/**
 * Checks if the subtask parameter is multiValue.
 *
 * @param {object} subtaskParameter - The subtask parameter object.
 * @returns {boolean} True if the subtask parameter is multiValue, false otherwise.
 * @throws {Error} If subtask parameter is null or undefined.
 */
export const isMultiValue = subtaskParameter => {
  if (isNil(subtaskParameter)) {
    throw new Error('subtaskParameter not defined');
  }
  if (!hasOptions(subtaskParameter)) {
    return false;
  }
  return get(subtaskParameter, 'multiValue', false);
};

/**
 * Gets the subtask parameter comments.
 *
 * @param {object} subtaskParameter - The subtask parameter object.
 * @returns {string} The comments for the subtask parameter or empty string if not found.
 * @throws {Error} If subtask parameter is null or undefined.
 */
export const getComments = subtaskParameter => {
  if (isNil(subtaskParameter)) {
    throw new Error('subtaskParameter not defined');
  }

  return get(subtaskParameter, 'comments', '');
};

/**
 * Gets the subtask parameter value.
 *
 * @param {object} subtaskParameter - The subtask parameter object.
 * @returns {string} The value for the subtask parameter or empty string if not found.
 * @throws {Error} If subtask parameter is null or undefined.
 */
export const getValue = subtaskParameter => {
  if (isNil(subtaskParameter)) {
    throw new Error('subtaskParameter not defined');
  }
  if (isMultiValue(subtaskParameter)) return get(subtaskParameter, 'value', []);
  return get(subtaskParameter, 'value', '');
};

/**
 * Checks if the subtask parameter is required.
 *
 * @param {object} subtaskParameter - The subtask parameter object.
 * @returns {boolean} True if the subtask parameter is required, false otherwise.
 * @throws {Error} If subtask parameter is null or undefined.
 */
export const isRequired = subtaskParameter => {
  if (isNil(subtaskParameter)) {
    throw new Error('subtaskParameter not defined');
  }
  return get(subtaskParameter, 'required', false);
};
